/** biome-ignore-all lint/a11y/noStaticElementInteractions: we know what we are doing here */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: we know what we are doing here */

import clsx from 'clsx';
import * as React from 'react';

import type { ContentKitContext } from '@gitbook/api-client';
import type { ContentKitRemote } from '@gitbook/contentkit-react';
import {
    BLOCK_CODE,
    createSectionIDs,
    getSectionIDs,
    isDocumentEmpty,
    moveToEdgeLeafBlock,
    type SectionIDs,
} from '@gitbook/doc-core';
import { useSlateCodeHighlighter } from '@gitbook/react-uikit';
import {
    type Change,
    type Document,
    isBlockRangeSelection,
    isTextRangeSelection,
    type SelectionRange,
    type TextRange,
    type Value,
} from '@gitbook/slate';
import { Editor, type EditorContainer, type Plugin } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
} from '@gitbook/spine-react';
import { List } from '@gitbook/util-immutable';
import { useMergedRefs } from '@gitbook/util-react-hooks';

import { setKeyboardShortcutsScope } from '../../Keyboard';
import { BlockLayout } from './BlockLayout';
import * as code from './code';
import * as columns from './columns';
import {
    dragDropPlugin,
    layoutPlugin,
    selectionHighlightingPlugin,
    useCopyPlugin,
    useFileDropPlugin,
    usePastePlugin,
    voidPlugin,
} from './common';
import { blockSelectionPlugin } from './common/blockSelectionPlugin';
import { CompletionContextProvider } from './completion/CompletionContext';
import { CompletionPrompt } from './completion/CompletionPrompt';
import {
    type BlockConfiguration,
    type BlockHighlightProps,
    ContentEditorContext,
    type ContentEditorContextValue,
    ContentEditorDefaultContext,
    EditorSectionIDsContext,
    type RightGutterComponentType,
} from './contexts';
import { EditorWidthLayout } from './EditorWidth';
import * as embed from './embed-integration';
import { emojiPlugin } from './emoji';
import * as expandable from './expandable';
import { FloatingFormatToolbar, formattingPlugin } from './format';
import { plugin as headingPlugin } from './heading';
import { inlineControlsPlugin } from './inline-controls';
import * as link from './link';
import { listPlugin } from './list';
import { codeBlockSyntax, markdownPlugins } from './markdown';
import { markPlugin } from './marks';
import { mentionPlugin } from './mention';
import * as paragraph from './paragraph';
import * as quoteHint from './quote-hint';
import * as realtime from './realtime';
import { mainPlugin } from './renderer';
import * as reusableContent from './reusable-content';
import * as stepper from './stepper';
import * as symbols from './symbols';
import * as table from './table';
import * as tabs from './tabs';
import * as textAlignment from './text-alignment';

export type ContentEditorProps = Partial<ContentEditorContextValue> & {
    /**
     * Other participant ranges to highlight.
     */
    selections?: List<SelectionRange>;

    /**
     * Value to render & edit.
     */
    value: Value;

    /**
     * Callback when document is updated.
     */
    onChange: (change: Change) => void;

    /**
     * Placeholder when content is empty
     */
    placeholder?: React.ReactNode;

    /**
     * Is the content editable ?
     */
    readOnly?: boolean;

    sectionIDs?: SectionIDs;

    /**
     * Which section ID is currently selected through navigation or other means, if any.
     */
    selectedSection: string | null;

    childrenClassName?: string;

    /**
     * Component to render in the right gutter of the editor. If undefined, nothing is rendered in the right gutter.
     */
    rightGutterComponent?: RightGutterComponentType;

    /**
     * Component to render to highlight blocks in the editor.
     */
    blockHighlightComponent?: React.ComponentType<BlockHighlightProps>;

    /**
     * Called when a block is hovered. If null is passed, it means nothing is hovered.
     */
    onBlockHover?: (key: string | null) => void;

    /**
     * Context to be used to while rendering integrations blocks.
     */
    integrationBlocksContext: ContentKitContext | null;

    /**
     * Interface to fetch remote content for integrations.
     */
    integrationBlocksRemote: ContentKitRemote;

    /**
     * Configuration for the blocks of the editor.
     */
    blocks: BlockConfiguration;

    /**
     * Extra plugins to add to the editor.
     */
    plugins?: Array<Plugin>;
};

/**
 * Editor component for documents.
 *
 * This component is memoized not only for performance, but also to solve
 * a bug with selection during history rewrite. See:
 * https://linear.app/gitbook-x/issue/RND-782/page-loses-focus-after-changing-the-title
 *
 * If removing the memoization of this component, make sure to test the above.
 */
export const ContentEditor = React.memo(
    React.forwardRef(function ContentEditorWithRef(
        props: ContentEditorProps,
        editorRefParam: React.Ref<EditorContainer>
    ): React.ReactElement {
        const {
            value,
            selections = List.create(),
            onChange,
            placeholder,
            logAnalyticsEvent,
            readOnly = false,
            sectionIDs,
            selectedSection,
            rightGutterComponent,
            blockHighlightComponent,
            integrationBlocksContext,
            integrationBlocksRemote,
            onBlockHover,
            blocks,
            plugins,
            copyPaste,
        } = props;
        const editorRef = React.useRef<EditorContainer>(null);
        const mergedRef = useMergedRefs(editorRef, editorRefParam);

        // We only support displaying text selections for now
        // https://linear.app/gitbook-x/issue/CUR-715/support-block-selections-from-other-users
        const textSelections = React.useMemo(
            () =>
                selections.filter(
                    (selection) => isTextRangeSelection(selection) && selection.isSet
                ) as List<TextRange>,
            [selections]
        );

        //
        // Create global context
        //
        const editorContext = React.useMemo<ContentEditorContextValue>(
            () => ({
                ...ContentEditorDefaultContext,
                ...(rightGutterComponent ? { rightGutterComponent } : {}),
                ...(blockHighlightComponent ? { blockHighlightComponent } : {}),
                onBlockHover,
                integrationBlocksContext,
                blocks,
                ...(copyPaste ? { copyPaste } : {}),
            }),
            [
                onBlockHover,
                integrationBlocksContext,
                blocks,
                rightGutterComponent,
                blockHighlightComponent,
                copyPaste,
            ]
        );

        if (logAnalyticsEvent) {
            editorContext.logAnalyticsEvent = logAnalyticsEvent;
        }

        editorContext.integrationBlocksRemote = integrationBlocksRemote;

        const fileDropPlugin = useFileDropPlugin(editorContext);
        const pastePlugin = usePastePlugin(editorContext);
        const copyPlugin = useCopyPlugin(editorContext);
        const tablePlugin = table.useTablePlugin();
        const pasteURLPlugin = link.usePasteURLPlugin(editorContext);
        const highlightingPlugin = useSlateCodeHighlighter(value.document, BLOCK_CODE);
        const reusableContentPlugin = reusableContent.usePlugin(editorContext);
        const allPlugins = React.useMemo(
            () => [
                mainPlugin,
                tablePlugin, // tablePlugin should be before voidPlugin to handle Enter correctly (because a table is a more specific void block)
                voidPlugin,
                formattingPlugin,
                codeBlockSyntax,
                code.plugin,
                columns.plugin,
                quoteHint.plugin,
                tabs.plugin, // tabs plugin should be after code/hint/quote to handle exiting blocks in tabs
                listPlugin,
                textAlignment.plugin, // needs to be before paragraph.plugin to handle backspace correctly
                paragraph.plugin, // needs to be after listPlugin so hitting Enter keeps the list context and creates another list item
                headingPlugin,
                pasteURLPlugin,
                ...markdownPlugins,
                ...symbols.plugins,
                mentionPlugin,
                emojiPlugin,
                inlineControlsPlugin,
                embed.insertPlugin,
                embed.captionPlugin,
                expandable.plugin,
                stepper.plugin,
                highlightingPlugin,
                realtime.plugin,
                ...(blocks.withReusableContent ? [reusableContentPlugin] : []),
                copyPlugin,
                fileDropPlugin,
                pastePlugin,
                dragDropPlugin,
                layoutPlugin,
                selectionHighlightingPlugin,
                blockSelectionPlugin,
                markPlugin,
                ...(plugins ?? []),
            ],
            [
                tablePlugin,
                pasteURLPlugin,
                highlightingPlugin,
                copyPlugin,
                fileDropPlugin,
                pastePlugin,
                blocks.withReusableContent,
                reusableContentPlugin,
                plugins,
            ]
        );

        const isEmpty = isDocumentEmpty(value.document);

        const { isFocused } = value;
        const { isCollapsed } = value.selection;

        /**
         * Whenever the editor is focused and the selection is collapsed, switch to the EDITOR keyboard scope
         * If the editor is focused and a range of text is selected, switch to TEXT
         * (this prevents any top-level shortcuts from conflicting with editing)
         */
        React.useEffect(() => {
            if (isFocused) {
                return setKeyboardShortcutsScope(
                    isCollapsed ? 'editor-selection-collapsed' : 'editor-selection-expanded'
                );
            }
        }, [isFocused, isCollapsed]);

        const lastSelectionModeRef = React.useRef<'block' | 'text' | null>(null);
        const selectionIsBlockRange = editorRef.current?.value.selection
            ? isBlockRangeSelection(editorRef.current.value.selection)
            : false;
        React.useEffect(() => {
            const selectionMode = selectionIsBlockRange ? 'block' : 'text';
            if (lastSelectionModeRef.current && selectionMode !== lastSelectionModeRef.current) {
                logAnalyticsEvent?.({
                    type: 'edit_switch_selection_mode',
                    selectionMode,
                });
            }
            lastSelectionModeRef.current = selectionIsBlockRange ? 'block' : 'text';
        }, [selectionIsBlockRange, logAnalyticsEvent]);

        const sectionIDsContext = React.useMemo(
            () => ({
                sections: sectionIDs || createSectionIDs(),
                selected: selectedSection,
            }),
            [sectionIDs, selectedSection]
        );

        return (
            <EditorSectionIDsContext.Provider value={sectionIDsContext}>
                <ContentEditorContext.Provider value={editorContext}>
                    <CompletionContextProvider
                        editorValue={value}
                        editorRef={editorRef}
                        readOnly={readOnly}
                    >
                        <EditorWidthLayout className="relative z-0 flex flex-col">
                            {/* Focus area at the top */}
                            {!readOnly && (
                                <FocusArea position="DOCUMENT_START" editorRef={editorRef} />
                            )}

                            {/* Placeholder */}
                            {isEmpty && placeholder && !value.isFocused && !readOnly ? (
                                <BlockLayout
                                    renderMode="centered"
                                    className="mx-auto w-full max-w-[--block-wrapper-max-width]"
                                >
                                    <div className="absolute top-2 text-content-paragraph text-muted">
                                        {placeholder}
                                    </div>
                                </BlockLayout>
                            ) : null}

                            {/* Content */}
                            <Editor
                                ref={mergedRef}
                                testID="page.contentEditor"
                                value={value}
                                onChange={onChange}
                                readOnly={readOnly}
                                selections={textSelections}
                                plugins={allPlugins}
                                virtualize
                                virtualizeChildren
                                className="ligatures-no-contextual"
                                enableBlockContainment={
                                    // Block containment enables performance optimizations around "isolating"
                                    // block elements. It's based on the CSS Containment model. We only enable
                                    // in readOnly mode as block containment is not compatible with the block controls
                                    // element.
                                    readOnly
                                }
                                nodeErrorFallback={nodeErrorFallback}
                                childrenClassName={props.childrenClassName}
                            >
                                {!readOnly && <FloatingFormatToolbar value={value} />}
                                {!readOnly && <CompletionPrompt document={value.document} />}
                            </Editor>

                            {/* Focus area at the bottom */}
                            {!readOnly && (
                                <FocusArea position="DOCUMENT_END" editorRef={editorRef} />
                            )}
                        </EditorWidthLayout>
                    </CompletionContextProvider>
                </ContentEditorContext.Provider>
            </EditorSectionIDsContext.Provider>
        );
    })
);

const FocusArea = React.memo(function FocusArea(props: {
    editorRef: React.RefObject<EditorContainer>;
    position: 'DOCUMENT_START' | 'DOCUMENT_END';
}) {
    const { editorRef, position } = props;
    return (
        <div
            tabIndex={-1}
            onClick={() => {
                editorRef.current?.change((change) => moveToEdgeLeafBlock(change, 'DOCUMENT_END'));
            }}
            className={clsx(
                'absolute inset-x-0 h-6 cursor-text',
                { DOCUMENT_START: '-top-6', DOCUMENT_END: '-bottom-6' }[position]
            )}
        />
    );
});

/**
 * Fallback component to render when reusable content fails to load.
 */
const nodeErrorFallback = (props: { resetError: () => void }) => (
    <div className="rounded border" contentEditable={false}>
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Alert />
            </EmptyStateIcon>
            <Heading>Error while loading block</Heading>
            <Description>This block could not be loaded.</Description>
            <EmptyStateActions>
                <Button label="Retry" onPress={() => props.resetError()} />
            </EmptyStateActions>
        </EmptyState>
    </div>
);

/**
 * Hook to compute section IDs for a document.
 */
export function useSectionIDs(document: Document): SectionIDs {
    const sectionIDsRef = React.useRef<SectionIDs>();
    const [sectionIDs, setSectionIDs] = React.useState<SectionIDs>(() =>
        getSectionIDs(document, sectionIDsRef.current)
    );
    React.useEffect(() => {
        const update = () => {
            sectionIDsRef.current = getSectionIDs(document, sectionIDsRef.current);
            setSectionIDs(sectionIDsRef.current);
        };
        if (typeof requestIdleCallback === 'function') {
            const cb = requestIdleCallback(update);
            return () => cancelIdleCallback(cb);
        }
        update();
    }, [document]);
    return sectionIDs;
}
